import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import apiService from "../../services/apiService";

const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid var(--vz-border-color)",
  background: "var(--vz-input-bg, var(--vz-secondary-bg, #fff))",
  color: "var(--vz-body-color)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const alertColors = {
  success: { color: "var(--vz-success)", bg: "rgba(var(--vz-success-rgb),.1)", border: "rgba(var(--vz-success-rgb),.3)", icon: "check-circle" },
  danger:  { color: "var(--vz-danger)",  bg: "rgba(var(--vz-danger-rgb),.1)",  border: "rgba(var(--vz-danger-rgb),.3)",  icon: "alert-circle"  },
  warning: { color: "var(--vz-warning)", bg: "rgba(var(--vz-warning-rgb),.1)", border: "rgba(var(--vz-warning-rgb),.3)", icon: "alert-triangle" },
  info:    { color: "var(--vz-info)",    bg: "rgba(var(--vz-info-rgb),.1)",    border: "rgba(var(--vz-info-rgb),.3)",    icon: "info"           },
};

const SoftwareBlockingForm = () => {
  const [activeTab, setActiveTab] = useState("block");
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  // Block form
  const [appName, setAppName]           = useState("");
  const [executable, setExecutable]     = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [blocking, setBlocking]         = useState(false);

  // Unblock form
  const [unblockExe, setUnblockExe]         = useState("");
  const [unblockAgents, setUnblockAgents]   = useState([]);
  const [unblocking, setUnblocking]         = useState(false);

  // Agent groups
  const [groups,              setGroups]              = useState([]);
  const [selectedGroup,       setSelectedGroup]       = useState(null);
  const [selectedUnblockGroup, setSelectedUnblockGroup] = useState(null);

  // DB-backed action log
  const [actionLog,      setActionLog]      = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [clearingHistory,    setClearingHistory]    = useState(false);
  const [clearConfirmOpen,   setClearConfirmOpen]   = useState(false);

  // Confirm modal (unblock confirm)
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null });

  // Alert
  const [alert, setAlert] = useState({ show: false, message: "", type: "info" });
// eslint-disable-next-line
  useEffect(() => { fetchAgents(); }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiService.get("/api/app-blocking/history");
      if (res?.success) setActionLog(res.history || []);
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  }, []);

  const clearHistory = async () => {
    setClearConfirmOpen(false);
    setClearingHistory(true);
    try {
      const res = await apiService.delete("/api/app-blocking/history");
      if (res?.success) {
        setActionLog([]);
        showAlert("Blocking history cleared.", "success");
      } else {
        showAlert(res?.error || "Failed to clear history.", "danger");
      }
    } catch (err) {
      showAlert(err?.message || "Failed to reach API.", "danger");
    } finally {
      setClearingHistory(false);
    }
  };

  const loadGroups = useCallback(async () => {
    try {
      const res = await apiService.get("/api/agent-groups");
      if (res?.success) setGroups(res.groups || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const showAlert = (message, type = "info") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "info" }), 5000);
  };

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const resp = await apiService.get("/api/wazuh/agents");
      setAgents(
        (resp.agents || [])
          .map(a => ({
            value: a.id,
            label: `${a.name} (${a.ip || "—"}) — ${a.status}`,
            hostname: a.name,
            status: a.status,
          }))
      );
    } catch {
      showAlert("Failed to load agents", "danger");
    } finally {
      setLoadingAgents(false);
    }
  };
// eslint-disable-next-line
  const onlineAgents = agents.filter(a => (a.status || "").toLowerCase().includes("active"));

  const targetAgentIds = (selected) =>
    selected.length > 0 ? selected.map(a => a.value) : agents.map(a => a.value);

  const groupOptions = groups.map((g) => ({
    value:      g.id,
    label:      g.name,
    agentCount: g.agent_ids.length,
    agentIds:   g.agent_ids,
  }));

  const handleGroupChange = (option) => {
    setSelectedGroup(option);
    if (option) {
      const matched = agents.filter((a) => option.agentIds.includes(a.value));
      setSelectedAgents(matched);
    } else {
      setSelectedAgents([]);
    }
  };

  const handleUnblockGroupChange = (option) => {
    setSelectedUnblockGroup(option);
    if (option) {
      const matched = agents.filter((a) => option.agentIds.includes(a.value));
      setUnblockAgents(matched);
    } else {
      setUnblockAgents([]);
    }
  };

  const handleBlock = async () => {
    if (!executable.trim()) { showAlert("Executable name is required", "warning"); return; }
    const agentIds = targetAgentIds(selectedAgents);
    if (agentIds.length === 0) { showAlert("No online agents available", "warning"); return; }
    try {
      setBlocking(true);
      const resp = await apiService.post("/api/app-blocking/block", {
        name: appName.trim() || executable.trim(),
        executable: executable.trim(),
        agent_ids: agentIds,
      });
      const count = resp.count || agentIds.length;
      showAlert(`Block command delivered to ${count} agent(s)`, "success");
      await loadHistory();
      setAppName(""); setExecutable(""); setSelectedAgents([]);
    } catch (err) {
      showAlert("Failed to block: " + err.message, "danger");
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    if (!unblockExe.trim()) { showAlert("Executable name is required", "warning"); return; }
    const agentIds = targetAgentIds(unblockAgents);
    if (agentIds.length === 0) { showAlert("No online agents available", "warning"); return; }
    try {
      setUnblocking(true);
      const resp = await apiService.post("/api/app-blocking/unblock", {
        executable: unblockExe.trim(),
        agent_ids: agentIds,
      });
      const count = resp.count || agentIds.length;
      showAlert(`Unblock command delivered to ${count} agent(s)`, "success");
      await loadHistory();
      setUnblockExe(""); setUnblockAgents([]);
    } catch (err) {
      showAlert("Failed to unblock: " + err.message, "danger");
    } finally {
      setUnblocking(false);
    }
  };

  const tabs = [
    { id: "block",   icon: "x-circle", label: "Block App"     },
    { id: "unblock", icon: "unlock",   label: "Unblock App"   },
    { id: "log",     icon: "clock",    label: `History${actionLog.length ? ` (${actionLog.length})` : ""}` },
  ];

  const ac = alertColors[alert.type] || alertColors.info;

  return (
    <>
      {alert.show && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: ac.bg, border: `1px solid ${ac.border}`, color: ac.color, fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <FeatherIcon icon={ac.icon} size={14} />
          <span style={{ flex: 1 }}>{alert.message}</span>
          <button onClick={() => setAlert({ ...alert, show: false })} style={{ background: "none", border: "none", cursor: "pointer", color: ac.color, padding: 0, fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
      )}

      <div style={card}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FeatherIcon icon="shield" size={15} style={{ color: "var(--vz-primary)" }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Application Blocking</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {actionLog.length > 0 && (
              <button onClick={() => setClearConfirmOpen(true)} disabled={clearingHistory}
                style={{ height: 30, padding: "0 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "rgba(var(--vz-danger-rgb),.06)", color: "var(--vz-danger)", border: "1px solid rgba(var(--vz-danger-rgb),.3)", display: "flex", alignItems: "center", gap: 4 }}>
                <FeatherIcon icon="trash-2" size={12} />
                Clear History
              </button>
            )}
            <button onClick={fetchAgents} disabled={loadingAgents}
              style={{ height: 30, padding: "0 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "transparent", color: "var(--vz-primary)", border: "1px solid var(--vz-primary)", display: "flex", alignItems: "center", gap: 4 }}>
              <FeatherIcon icon="refresh-cw" size={12} style={{ animation: loadingAgents ? "spin 1s linear infinite" : "none" }} />
              Refresh Agents
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--vz-border-color)", background: "rgba(var(--vz-primary-rgb),.02)" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{
                padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
                fontSize: 13, fontWeight: activeTab === t.id ? 700 : 400,
                color: activeTab === t.id ? "var(--vz-primary)" : "var(--vz-secondary-color)",
                borderBottom: activeTab === t.id ? "2px solid var(--vz-primary)" : "2px solid transparent",
                display: "flex", alignItems: "center", gap: 6,
              }}>
              <FeatherIcon icon={t.icon} size={13} />
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px 22px" }}>
          {/* BLOCK TAB */}
          {activeTab === "block" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".04em" }}>App Name <span style={{ color: "var(--vz-secondary-color)", fontWeight: 400 }}>(optional)</span></p>
                  <input style={inputStyle} type="text" placeholder="e.g., AnyDesk"
                    value={appName} onChange={e => setAppName(e.target.value)} disabled={blocking} />
                  <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "4px 0 0" }}>Friendly display name</p>
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".04em" }}>Executable <span style={{ color: "var(--vz-danger)" }}>*</span></p>
                  <input style={inputStyle} type="text" placeholder="e.g., AnyDesk.exe"
                    value={executable} onChange={e => setExecutable(e.target.value)} disabled={blocking} />
                  <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "4px 0 0" }}>Exact filename including .exe</p>
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                {/* Group Policy selector */}
                {groups.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".04em" }}>Group Policy</p>
                    <Select
                      isClearable
                      options={groupOptions}
                      value={selectedGroup}
                      onChange={handleGroupChange}
                      placeholder="Select a group to target all agents in it…"
                      isDisabled={blocking}
                      styles={{
                        control: base => ({ ...base, background: "var(--vz-input-bg, var(--vz-secondary-bg))", borderColor: "var(--vz-border-color)", fontSize: 13 }),
                        menu: base => ({ ...base, background: "var(--vz-secondary-bg, #fff)", zIndex: 9999 }),
                        option: (base, s) => ({ ...base, background: s.isSelected ? "var(--vz-primary)" : s.isFocused ? "rgba(var(--vz-primary-rgb),.08)" : "transparent", color: s.isSelected ? "#fff" : "var(--vz-body-color)", fontSize: 13 }),
                        singleValue: base => ({ ...base, color: "var(--vz-body-color)" }),
                        input: base => ({ ...base, color: "var(--vz-body-color)" }),
                      }}
                      formatOptionLabel={(opt) => (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: 600 }}>{opt.label}</span>
                          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginLeft: 8 }}>{opt.agentCount} agent{opt.agentCount !== 1 ? "s" : ""}</span>
                        </div>
                      )}
                    />
                  </div>
                )}

                {/* Divider */}
                {groups.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em" }}>or select individually</span>
                    <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: 0, textTransform: "uppercase", letterSpacing: ".04em" }}>Target Agents</p>
                  <button
                    onClick={() => selectedAgents.length === agents.length ? setSelectedAgents([]) : setSelectedAgents(agents)}
                    disabled={blocking || loadingAgents}
                    style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-primary)", background: "transparent", border: "1px solid var(--vz-primary)", borderRadius: 6, padding: "2px 10px", cursor: "pointer" }}>
                    {selectedAgents.length === agents.length ? "Clear All" : "Select All"}
                  </button>
                </div>
                <Select isMulti options={agents} value={selectedAgents}
                  onChange={(v) => { setSelectedAgents(v); setSelectedGroup(null); }}
                  isLoading={loadingAgents} isDisabled={blocking}
                  placeholder="Select agents or use Select All..."
                  styles={{
                    control: base => ({ ...base, background: "var(--vz-input-bg, var(--vz-secondary-bg))", borderColor: "var(--vz-border-color)", fontSize: 13 }),
                    multiValue: base => ({ ...base, background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: 6 }),
                    multiValueLabel: base => ({ ...base, color: "var(--vz-body-color)", fontSize: 12 }),
                    multiValueRemove: base => ({ ...base, color: "var(--vz-secondary-color)", ":hover": { background: "rgba(var(--vz-danger-rgb),.1)", color: "var(--vz-danger)" } }),
                  }}
                />
                <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: "4px 0 0" }}>
                  {selectedAgents.length > 0 ? `Will block on ${selectedAgents.length} selected agent(s)` : `Will block on all ${agents.length} agent(s)`}
                </p>
              </div>

              <button onClick={handleBlock} disabled={blocking || !executable.trim()}
                style={{ height: 40, padding: "0 24px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: blocking || !executable.trim() ? "not-allowed" : "pointer", background: "var(--vz-danger)", color: "#fff", border: "none", display: "flex", alignItems: "center", gap: 8, opacity: blocking || !executable.trim() ? 0.65 : 1 }}>
                {blocking ? <><Spinner size="sm" /> Sending...</> : <><FeatherIcon icon="x-circle" size={15} /> Block Application</>}
              </button>
            </div>
          )}

          {/* UNBLOCK TAB */}
          {activeTab === "unblock" && (
            <div>
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".04em" }}>Executable <span style={{ color: "var(--vz-danger)" }}>*</span></p>
                <input style={inputStyle} type="text" placeholder="e.g., AnyDesk.exe"
                  value={unblockExe} onChange={e => setUnblockExe(e.target.value)} disabled={unblocking} />
              </div>

              <div style={{ marginBottom: 18 }}>
                {/* Group Policy selector */}
                {groups.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".04em" }}>Group Policy</p>
                    <Select
                      isClearable
                      options={groupOptions}
                      value={selectedUnblockGroup}
                      onChange={handleUnblockGroupChange}
                      placeholder="Select a group to target all agents in it…"
                      isDisabled={unblocking}
                      styles={{
                        control: base => ({ ...base, background: "var(--vz-input-bg, var(--vz-secondary-bg))", borderColor: "var(--vz-border-color)", fontSize: 13 }),
                        menu: base => ({ ...base, background: "var(--vz-secondary-bg, #fff)", zIndex: 9999 }),
                        option: (base, s) => ({ ...base, background: s.isSelected ? "var(--vz-primary)" : s.isFocused ? "rgba(var(--vz-primary-rgb),.08)" : "transparent", color: s.isSelected ? "#fff" : "var(--vz-body-color)", fontSize: 13 }),
                        singleValue: base => ({ ...base, color: "var(--vz-body-color)" }),
                        input: base => ({ ...base, color: "var(--vz-body-color)" }),
                      }}
                      formatOptionLabel={(opt) => (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: 600 }}>{opt.label}</span>
                          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginLeft: 8 }}>{opt.agentCount} agent{opt.agentCount !== 1 ? "s" : ""}</span>
                        </div>
                      )}
                    />
                  </div>
                )}

                {/* Divider */}
                {groups.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-secondary-color)", textTransform: "uppercase", letterSpacing: ".05em" }}>or select individually</span>
                    <div style={{ flex: 1, height: 1, background: "var(--vz-border-color)" }} />
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "var(--vz-body-color)", margin: 0, textTransform: "uppercase", letterSpacing: ".04em" }}>Target Agents</p>
                  <button
                    onClick={() => unblockAgents.length === agents.length ? setUnblockAgents([]) : setUnblockAgents(agents)}
                    disabled={unblocking || loadingAgents}
                    style={{ fontSize: 11, fontWeight: 600, color: "var(--vz-primary)", background: "transparent", border: "1px solid var(--vz-primary)", borderRadius: 6, padding: "2px 10px", cursor: "pointer" }}>
                    {unblockAgents.length === agents.length ? "Clear All" : "Select All"}
                  </button>
                </div>
                <Select isMulti options={agents} value={unblockAgents}
                  onChange={(v) => { setUnblockAgents(v); setSelectedUnblockGroup(null); }}
                  isLoading={loadingAgents} isDisabled={unblocking}
                  placeholder="Search and select agents…"
                  styles={{
                    control: base => ({ ...base, background: "var(--vz-input-bg, var(--vz-secondary-bg))", borderColor: "var(--vz-border-color)", fontSize: 13 }),
                    multiValue: base => ({ ...base, background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: 6 }),
                    multiValueLabel: base => ({ ...base, color: "var(--vz-body-color)", fontSize: 12 }),
                    multiValueRemove: base => ({ ...base, color: "var(--vz-secondary-color)", ":hover": { background: "rgba(var(--vz-danger-rgb),.1)", color: "var(--vz-danger)" } }),
                  }}
                />
              </div>

              <button onClick={() => setConfirmModal({ open: true, action: "unblock" })} disabled={unblocking || !unblockExe.trim()}
                style={{ height: 40, padding: "0 24px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: unblocking || !unblockExe.trim() ? "not-allowed" : "pointer", background: "var(--vz-success)", color: "#fff", border: "none", display: "flex", alignItems: "center", gap: 8, opacity: unblocking || !unblockExe.trim() ? 0.65 : 1 }}>
                {unblocking ? <><Spinner size="sm" /> Sending...</> : <><FeatherIcon icon="unlock" size={15} /> Unblock Application</>}
              </button>
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === "log" && (
            <div>
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Spinner size="sm" color="primary" />
                </div>
              ) : actionLog.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--vz-secondary-color)" }}>
                  <FeatherIcon icon="clock" size={36} style={{ opacity: 0.25, display: "block", margin: "0 auto 12px" }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No actions recorded yet.</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {actionLog.map(entry => {
                    const isBlock = entry.action === "block";
                    const labels  = entry.agent_labels?.join(", ") || entry.agent_ids?.join(", ") || "";
                    const ts      = entry.created_at ? new Date(entry.created_at).toLocaleString() : "";
                    return (
                      <div key={entry.id} style={{ padding: "12px 14px", borderRadius: 8, border: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                          background: isBlock ? "rgba(var(--vz-danger-rgb),.1)" : "rgba(var(--vz-success-rgb),.1)",
                          color: isBlock ? "var(--vz-danger)" : "var(--vz-success)",
                        }}>
                          <FeatherIcon icon={isBlock ? "x-circle" : "unlock"} size={15} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--vz-body-color)" }}>
                            <span style={{ color: isBlock ? "var(--vz-danger)" : "var(--vz-success)", textTransform: "capitalize" }}>{entry.action}ed</span>
                            {" "}<code style={{ fontSize: 12 }}>{entry.executable}</code>
                            {entry.app_name && entry.app_name !== entry.executable && (
                              <span style={{ color: "var(--vz-secondary-color)", fontWeight: 400 }}> ({entry.app_name})</span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", marginTop: 2 }}>
                            {labels} · {entry.affected} agent(s) · {ts}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Unblock Confirm Modal */}
      <Modal isOpen={confirmModal.open} toggle={() => setConfirmModal({ open: false, action: null })} centered>
        <ModalHeader toggle={() => setConfirmModal({ open: false, action: null })}>
          Confirm Unblock
        </ModalHeader>
        <ModalBody>
          <p style={{ margin: 0, fontSize: 14 }}>
            Unblock <code>{unblockExe}</code> on{" "}
            {unblockAgents.length > 0 ? `${unblockAgents.length} selected agent(s)` : `all ${agents.length} online agent(s)`}?
          </p>
        </ModalBody>
        <ModalFooter>
          <button onClick={() => setConfirmModal({ open: false, action: null })}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--vz-border-color)", background: "transparent", color: "var(--vz-body-color)", cursor: "pointer", fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={() => { setConfirmModal({ open: false, action: null }); handleUnblock(); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "var(--vz-success)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, marginLeft: 8 }}>
            <FeatherIcon icon="unlock" size={13} style={{ marginRight: 6 }} /> Unblock
          </button>
        </ModalFooter>
      </Modal>

      {/* Clear History Confirm Modal */}
      <Modal isOpen={clearConfirmOpen} toggle={() => setClearConfirmOpen(false)} centered size="sm">
        <ModalHeader toggle={() => setClearConfirmOpen(false)} style={{ borderBottom: "1px solid var(--vz-border-color)" }}>
          Clear History
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(var(--vz-danger-rgb),.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FeatherIcon icon="trash-2" size={16} style={{ color: "var(--vz-danger)" }} />
            </div>
            <div>
              <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: 14, color: "var(--vz-body-color)" }}>Clear all blocking history?</p>
              <p style={{ margin: 0, fontSize: 13, color: "var(--vz-secondary-color)" }}>
                This permanently deletes all {actionLog.length} record{actionLog.length !== 1 ? "s" : ""} from the database. This cannot be undone.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid var(--vz-border-color)", gap: 8 }}>
          <button onClick={() => setClearConfirmOpen(false)}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--vz-border-color)", background: "transparent", color: "var(--vz-body-color)", cursor: "pointer", fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={clearHistory}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none", background: "var(--vz-danger)", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            <FeatherIcon icon="trash-2" size={13} /> Clear All
          </button>
        </ModalFooter>
      </Modal>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default SoftwareBlockingForm;
